const DNI_CONTROL_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const DNI_REGEX = /^[0-9]{8}[A-Z]$/i;
const NIE_REGEX = /^[XYZ][0-9]{7}[A-Z]$/i;
const NIF_LEGAL_REGEX = /^[ABCDEFGHJKLMNPQRSUVW][0-9]{7}[0-9A-J]$/i;

export interface DocumentValidationResult {
  isValid: boolean;
  documentType?: 'DNI' | 'NIE' | 'NIF';
  error?: string;
}

const calculateControlLetter = (numericPart: number): string => {
  return DNI_CONTROL_LETTERS[numericPart % 23];
};

export const validateDNI = (document: string): boolean => {
  const upperDoc = document.toUpperCase().trim();

  if (!DNI_REGEX.test(upperDoc)) {
    return false;
  }

  const numericPart = parseInt(upperDoc.slice(0, 8), 10);
  const providedLetter = upperDoc.slice(8);
  const expectedLetter = calculateControlLetter(numericPart);

  return providedLetter === expectedLetter;
};

export const validateNIE = (document: string): boolean => {
  const upperDoc = document.toUpperCase().trim();

  if (!NIE_REGEX.test(upperDoc)) {
    return false;
  }

  const initialLetter = upperDoc[0];
  let numericPrefix: string;

  switch (initialLetter) {
    case 'X':
      numericPrefix = '0';
      break;
    case 'Y':
      numericPrefix = '1';
      break;
    case 'Z':
      numericPrefix = '2';
      break;
    default:
      return false;
  }

  const numericPart = parseInt(numericPrefix + upperDoc.slice(1, 8), 10);
  const providedLetter = upperDoc.slice(8);
  const expectedLetter = calculateControlLetter(numericPart);

  return providedLetter === expectedLetter;
};

const NIF_CONTROL_LETTERS = 'JABCDEFGHI';
const LETTER_ONLY_TYPES = ['K', 'L', 'M', 'P', 'Q', 'S'];
const DIGIT_ONLY_TYPES = ['A', 'B', 'E', 'H'];

export const validateNIFLegal = (document: string): boolean => {
  const upperDoc = document.toUpperCase().trim();

  if (!NIF_LEGAL_REGEX.test(upperDoc)) {
    return false;
  }

  const digits = upperDoc.slice(1, 8);

  const { sumOdd, sumEven } = digits.split('').reduce(
    (acc, char, i) => {
      const digit = parseInt(char, 10);

      if (i % 2 === 0) {
        const doubled = digit * 2;
        const doubledResult = doubled > 9 ? doubled - 9 : doubled;

        return { ...acc, sumOdd: acc.sumOdd + doubledResult };
      }

      return { ...acc, sumEven: acc.sumEven + digit };
    },
    { sumOdd: 0, sumEven: 0 }
  );

  const totalSum = sumOdd + sumEven;
  const controlDigit = (10 - (totalSum % 10)) % 10;
  const controlChar = upperDoc[8];

  const entityType = upperDoc[0];

  if (LETTER_ONLY_TYPES.includes(entityType)) {
    return controlChar === NIF_CONTROL_LETTERS[controlDigit];
  }

  if (DIGIT_ONLY_TYPES.includes(entityType)) {
    return controlChar === controlDigit.toString();
  }

  return controlChar === controlDigit.toString() || controlChar === NIF_CONTROL_LETTERS[controlDigit];
};

export const detectDocumentType = (document: string): 'DNI' | 'NIE' | 'NIF' | null => {
  const upperDoc = document.toUpperCase().trim();

  if (DNI_REGEX.test(upperDoc)) {
    return 'DNI';
  }

  if (NIE_REGEX.test(upperDoc)) {
    return 'NIE';
  }

  if (NIF_LEGAL_REGEX.test(upperDoc)) {
    return 'NIF';
  }

  return null;
};

export const validateSpanishDocument = (document: string): DocumentValidationResult => {
  if (!document || typeof document !== 'string') {
    return {
      isValid: false,
      error: 'EMPTY_DOCUMENT'
    };
  }

  const cleanDocument = document.toUpperCase().trim();

  if (cleanDocument.length === 0) {
    return {
      isValid: false,
      error: 'EMPTY_DOCUMENT'
    };
  }

  const documentType = detectDocumentType(cleanDocument);

  if (!documentType) {
    return {
      isValid: false,
      error: 'INVALID_FORMAT'
    };
  }

  let isValid = false;

  switch (documentType) {
    case 'DNI':
      isValid = validateDNI(cleanDocument);
      break;
    case 'NIE':
      isValid = validateNIE(cleanDocument);
      break;
    case 'NIF':
      isValid = validateNIFLegal(cleanDocument);
      break;
  }

  if (!isValid) {
    return {
      isValid: false,
      documentType,
      error: 'INVALID_CONTROL_LETTER'
    };
  }

  return {
    isValid: true,
    documentType
  };
};

export const isValidSpanishDocument = (document: string): boolean => {
  return validateSpanishDocument(document).isValid;
};
